import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import AddLineIcon from "remixicon-react/AddLineIcon";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import SaveIcon from "@mui/icons-material/Save";
import { SyncAltOutlined } from "@mui/icons-material";

import { useQuery } from "@tanstack/react-query";
import EntrepriseForm from "$containers/Entreprises/EntrepriseForm";
import { Text, Popin, Button, InfoContainer } from "$components";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";

import useCompanyForm from "$hooks/useCompanyForm";

import { CompanyType, DeliveryType, ShippingRecord } from "$types";
import { getShippingRecordDeliveryTypeLabel } from "$utils/typeConstants";
import { normalizeCarrierTrackingLink } from "$utils/utils";
import useCompanyOptions from "./useCompanyOptions";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

type FormValues = Pick<
  ShippingRecord,
  | "carrierReference"
  | "carrierCompanyId"
  | "commissionCompanyId"
  | "agencyCompanyId"
  | "notes"
  | "withGasoil"
  | "price"
>;

type CompanyTypeField = keyof Pick<
  FormValues,
  "agencyCompanyId" | "commissionCompanyId" | "carrierCompanyId"
>;

type Props = {
  classes: any;
  shippingRecord: ShippingRecord;
  updateShippingRecord: (values: Partial<ShippingRecord>) => void;
  disabled?: boolean;
};

function ShippingRecordInfoBlock({
  classes,
  shippingRecord,
  updateShippingRecord,
  disabled = false,
}: Props) {
  const handleOnCompanyCreated = (companyId: string) => {
    setValue(selectedCompanyTypeToCreate, companyId);
  };
  const {
    submitting: isCreatingCompany,
    handleSubmit: handleCompanySubmit,
    togglePopin: toggleCompanyDrawer,
    isOpened: isCompanyDrawerOpen,
  } = useCompanyForm(handleOnCompanyCreated);
  const [selectedCompanyTypeToCreate, setSelectedCompanyTypeToCreate] =
    useState<CompanyTypeField>("carrierCompanyId");
  const savedCarrierTrackingLink = shippingRecord?.carrierTrackingLink ?? "";
  const [isEditingCarrierTrackingLink, setIsEditingCarrierTrackingLink] =
    useState(!savedCarrierTrackingLink);
  const [carrierTrackingLinkDraft, setCarrierTrackingLinkDraft] = useState(
    savedCarrierTrackingLink
  );
  const defaultFormValues: FormValues = useMemo(
    () => ({
      carrierReference: shippingRecord?.carrierReference,
      carrierCompanyId: shippingRecord?.carrierCompanyId,
      commissionCompanyId: shippingRecord?.commissionCompanyId,
      agencyCompanyId: shippingRecord?.agencyCompanyId,
      notes: shippingRecord?.notes,
      withGasoil: shippingRecord?.withGasoil,
      price: shippingRecord?.price,
    }),
    [shippingRecord]
  );
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });
  const deliveryType = shippingRecord?.deliveryType;
  const carrierCompanyId = watch("carrierCompanyId");
  const price = watch("price");

  const { agencyOptions, carrierOptions } = useCompanyOptions(
    deliveryType,
    carrierCompanyId
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [defaultFormValues, reset]);

  useEffect(() => {
    const link = shippingRecord?.carrierTrackingLink ?? "";
    setCarrierTrackingLinkDraft(link);
    setIsEditingCarrierTrackingLink(!link);
  }, [shippingRecord?.carrierTrackingLink]);

  const isCommissionCompanySelectDisplayed =
    deliveryType && deliveryType === DeliveryType.CHARTERING;
  const isCarrierCompanySelectDisplayed =
    deliveryType && deliveryType !== DeliveryType.PROVISIONING;
  const isAgencyCompanySelectDisplayed =
    deliveryType &&
    deliveryType !== DeliveryType.CHARTERING &&
    deliveryType !== DeliveryType.PROVISIONING;
  const isCarrierCompanySelectDisabled =
    deliveryType === DeliveryType.EXPRESS ||
    deliveryType === DeliveryType.MESSAGING;
  const isAgencyCompanySelectDisabled =
    deliveryType === DeliveryType.EXPRESS ||
    deliveryType === DeliveryType.MESSAGING;
  const isPriceBlockDisplayed =
    deliveryType === DeliveryType.CHARTERING ||
    deliveryType === DeliveryType.COURIER;

  const isCarrierTrackingLinkWide =
    isCommissionCompanySelectDisplayed && isCarrierCompanySelectDisplayed;

  const { data: selectedCarrierCompany } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, carrierCompanyId],
    queryFn: () => CompanyServices.getCompanyById(carrierCompanyId),
    enabled: !!carrierCompanyId,
    staleTime: 60 * 1000,
  });

  const onSubmit = (values: FormValues) => {
    updateShippingRecord(values);
  };

  const handleAddCompanyClick = (companyTypeField: CompanyTypeField) => {
    setSelectedCompanyTypeToCreate(companyTypeField);
    toggleCompanyDrawer(true);
  };

  const handleChangeRadio = (e: any, value: string) => {
    setValue("withGasoil", value === "true");
    handleSubmit(onSubmit)();
  };

  const handleChangeCarrier = (
    formKey: keyof FormValues,
    carrierId: string
  ) => {
    setValue(formKey, carrierId);
    handleSubmit(onSubmit)();
  };

  const handleSaveCarrierTrackingLink = () => {
    const normalizedLink = normalizeCarrierTrackingLink(
      carrierTrackingLinkDraft
    );
    if (!normalizedLink) return;
    setCarrierTrackingLinkDraft(normalizedLink);
    updateShippingRecord({ carrierTrackingLink: normalizedLink });
    setIsEditingCarrierTrackingLink(false);
  };

  const handleOpenCarrierTrackingLink = () => {
    const normalizedLink = normalizeCarrierTrackingLink(
      carrierTrackingLinkDraft
    );
    if (!normalizedLink) return;
    window.open(normalizedLink, "_blank", "noopener,noreferrer");
  };

  const renderCarrierTrackingLinkField = () => (
    <>
      <div className={classes.selectContainer}>
        {isEditingCarrierTrackingLink ? (
          <TextField
            style={{ width: "100%" }}
            label="Lien de suivi transporteur"
            disabled={disabled}
            variant="outlined"
            value={carrierTrackingLinkDraft}
            onChange={(e) => setCarrierTrackingLinkDraft(e.target.value)}
            InputLabelProps={{
              shrink: Boolean(carrierTrackingLinkDraft),
            }}
          />
        ) : (
          <FormControl
            variant="outlined"
            fullWidth
            disabled={disabled}
            sx={{ m: 0 }}
          >
            <InputLabel shrink htmlFor="carrier-tracking-link-cta">
              Lien de suivi transporteur
            </InputLabel>
            <Button
              id="carrier-tracking-link-cta"
              variant="contained"
              color="primary"
              disableElevation
              disabled={!carrierTrackingLinkDraft.trim()}
              onClick={handleOpenCarrierTrackingLink}
              className={classes.carrierTrackingLinkButton}
            >
              {carrierTrackingLinkDraft}
            </Button>
          </FormControl>
        )}
      </div>
      <div className={classes.addButtonContainer}>
        <Button
          disabled={
            disabled ||
            (isEditingCarrierTrackingLink && !carrierTrackingLinkDraft.trim())
          }
          className={classes.addButton}
          onClick={
            isEditingCarrierTrackingLink
              ? handleSaveCarrierTrackingLink
              : () => setIsEditingCarrierTrackingLink(true)
          }
        >
          {isEditingCarrierTrackingLink ? <SaveIcon /> : <Edit2LineIcon />}
        </Button>
      </div>
    </>
  );

  return (
    <div>
      <InfoContainer
        className={classes.paperContainer}
        leftHeader={
          <div className={classes.infoHeader}>
            <SyncAltOutlined />
            <Text type="h3">Informations suivi</Text>
          </div>
        }
      >
        <div className={classes.lineContainersStack}>
          <div className={classes.lineContainerInStack}>
            <div className={classes.inputContainer}>
              <TextField
                style={{ width: "100%" }}
                label="Type d'envoi"
                disabled
                variant="outlined"
                InputLabelProps={{
                  shrink: Boolean(shippingRecord?.deliveryType),
                }}
                value={getShippingRecordDeliveryTypeLabel(
                  shippingRecord?.deliveryType
                )}
              />
            </div>
            {shippingRecord?.deliveryType !== DeliveryType.PROVISIONING ? (
              <div className={classes.inputContainer}>
                <CField
                  disabled={disabled}
                  label="Ref transporteur"
                  control={control}
                  name="carrierReference"
                  onBlur={handleSubmit(onSubmit)}
                />
              </div>
            ) : (
              <>
                <div className={classes.inputContainer} />
                <div className={classes.inputContainer} />
              </>
            )}
            {isPriceBlockDisplayed && (
              <div className={classes.priceContainer}>
                <div>
                  <CField
                    disabled={disabled}
                    control={control}
                    label="Tarif"
                    name="withGasoil"
                    error={errors.withGasoil}
                  >
                    {(props: CFieldInputProps) => (
                      <RadioGroup
                        row
                        aria-label="gazole"
                        {...props}
                        onChange={handleChangeRadio}
                      >
                        <FormControlLabel
                          disabled={disabled}
                          value="false"
                          checked={!props.value}
                          control={<Radio color="primary" />}
                          label="Hors gazole"
                        />
                        <FormControlLabel
                          disabled={disabled}
                          value="true"
                          checked={props.value}
                          control={<Radio color="primary" />}
                          label="Avec gazole"
                        />
                      </RadioGroup>
                    )}
                  </CField>
                </div>
                <div>
                  <NumericFormat
                    disabled={disabled}
                    label="Tarif"
                    customInput={TextField}
                    variant="outlined"
                    thousandSeparator=" "
                    decimalSeparator=","
                    decimalScale={2}
                    value={price}
                    onValueChange={(values: NumberFormatValues) => {
                      setValue("price", values.floatValue);
                    }}
                    onBlur={handleSubmit(onSubmit)}
                    InputProps={{
                      endAdornment: <span>€</span>,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className={classes.lineContainerInStack}>
            {isCommissionCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <div className={classes.selectContainer}>
                  <CField
                    disabled={disabled}
                    control={control}
                    name="commissionCompanyId"
                    error={errors.commissionCompanyId}
                  >
                    {(props: CFieldInputProps) => (
                      <CompanySelect
                        {...props}
                        placeholder=""
                        label="Commissionnaire"
                        companyOptions={carrierOptions}
                        onChange={(carrierId: string) =>
                          handleChangeCarrier("commissionCompanyId", carrierId)
                        }
                        ignoreSideBarCompanyFilter
                        minCharsToTrigerSearch={0}
                        rowsPerPage={99999}
                      />
                    )}
                  </CField>
                </div>
                <div className={classes.addButtonContainer}>
                  <Button
                    disabled={disabled}
                    className={classes.addButton}
                    onClick={() => handleAddCompanyClick("commissionCompanyId")}
                  >
                    <AddLineIcon />
                  </Button>
                </div>
              </div>
            )}
            {isCarrierCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <div className={classes.selectContainer}>
                  <CField
                    control={control}
                    name="carrierCompanyId"
                    disabled={isCarrierCompanySelectDisabled || disabled}
                    error={errors.carrierCompanyId}
                  >
                    {(props: CFieldInputProps) => (
                      <CompanySelect
                        {...props}
                        placeholder=""
                        label="Transporteur"
                        companyOptions={carrierOptions}
                        onChange={(carrierId: string) =>
                          handleChangeCarrier("carrierCompanyId", carrierId)
                        }
                        ignoreSideBarCompanyFilter
                        minCharsToTrigerSearch={0}
                        rowsPerPage={99999}
                      />
                    )}
                  </CField>
                </div>
                <div className={classes.addButtonContainer}>
                  <Button
                    disabled={isCarrierCompanySelectDisabled || disabled}
                    className={classes.addButton}
                    onClick={() => handleAddCompanyClick("carrierCompanyId")}
                  >
                    <AddLineIcon />
                  </Button>
                </div>
              </div>
            )}
            {isAgencyCompanySelectDisplayed && (
              <div className={classes.inputContainer}>
                <div className={classes.selectContainer}>
                  <CField
                    control={control}
                    name="agencyCompanyId"
                    disabled={isAgencyCompanySelectDisabled || disabled}
                    error={errors.agencyCompanyId}
                  >
                    {(props: CFieldInputProps) => (
                      <CompanySelect
                        {...props}
                        placeholder=""
                        label="Agence"
                        companyOptions={agencyOptions}
                        onChange={(carrierId: string) =>
                          handleChangeCarrier("agencyCompanyId", carrierId)
                        }
                        minCharsToTrigerSearch={0}
                        rowsPerPage={99999}
                      />
                    )}
                  </CField>
                </div>
                <div className={classes.addButtonContainer}>
                  <Button
                    disabled={isAgencyCompanySelectDisabled || disabled}
                    className={classes.addButton}
                    onClick={() => handleAddCompanyClick("agencyCompanyId")}
                  >
                    <AddLineIcon />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className={classes.lineContainerInStack}>
            <div
              className={
                isCarrierTrackingLinkWide
                  ? classes.carrierTrackingLinkContainer
                  : classes.inputContainer
              }
            >
              {renderCarrierTrackingLinkField()}
            </div>
            {isAgencyCompanySelectDisplayed && (
              <div className={classes.inputContainer} aria-hidden />
            )}
          </div>
        </div>
      </InfoContainer>
      <InfoContainer
        className={classes.paperContainer}
        leftHeader={
          <div className={classes.infoHeader}>
            <Edit2LineIcon />
            <Text type="h3">Remarques</Text>
          </div>
        }
      >
        <div className={classes.lineContainer}>
          <CField
            disabled={disabled}
            label=""
            control={control}
            name="notes"
            multiline
            variant="outlined"
            maxRows={3}
            minRows={3}
            onBlur={handleSubmit(onSubmit)}
          />
        </div>
      </InfoContainer>
      <Popin openPopin={isCompanyDrawerOpen} toggleDrawer={toggleCompanyDrawer}>
        <EntrepriseForm
          onSubmit={handleCompanySubmit}
          onReset={() => toggleCompanyDrawer(false)}
          isSubmitting={isCreatingCompany}
          readOnlyFields={["agencyOf", "companyTypes", "contributorOf"]}
          defaultValues={{
            companyTypes:
              selectedCompanyTypeToCreate === "agencyCompanyId"
                ? [CompanyType.AGENCY]
                : [CompanyType.CARRIER],
            ...(selectedCompanyTypeToCreate === "agencyCompanyId" && {
              agencyOf: selectedCarrierCompany,
            }),
          }}
        />
      </Popin>
    </div>
  );
}

export default ShippingRecordInfoBlock;
